import React, { useEffect, useMemo, useRef, useState } from "react";
import YouTube from "react-youtube";
import { useVideoPlayer } from "./VideoPlayerProvider";

const OFFSET = 8;

export default function GlobalVideoPlayer() {
  const {
    videoId,
    isVisible,
    isExpanded,
    isPlaying,
    setPlayerObject,
    setIsPlaying,
    stop,
    expand,
    minimize,
    pause,
    resume,
  } = useVideoPlayer();

  const wrapperRef = useRef(null);
  const handleRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0, left: 0, top: 0 });
  const [dragging, setDragging] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const fsWasPlayingRef = useRef(false);

  useEffect(() => {
    setHasStarted(false);
  }, [videoId]);

  const sizes = useMemo(
    () => ({
      mini: { w: 320, h: 180 },
      expanded: {
        w: window.innerWidth - OFFSET * 2,
        h: Math.floor(((window.innerWidth - OFFSET * 2) * 9) / 16),
      },
    }),
    []
  );

  const current = isExpanded ? sizes.expanded : sizes.mini;

  useEffect(() => {
    if (isExpanded) {
      setPos({
        x: OFFSET,
        y: Math.max(
          OFFSET,
          Math.floor((window.innerHeight - sizes.expanded.h) / 2)
        ),
      });
    }
  }, [isExpanded, sizes.expanded.h]);

  const computeBottomRight = () => ({
    x: Math.max(OFFSET, window.innerWidth - current.w - OFFSET),
    y: Math.max(OFFSET, window.innerHeight - current.h - OFFSET),
  });

  const [pos, setPos] = useState(() => {
    try {
      const s = localStorage.getItem("kk_player_pos");
      if (s) return JSON.parse(s);
    } catch {}
    return computeBottomRight();
  });

  useEffect(() => {
    try {
      localStorage.setItem("kk_player_pos", JSON.stringify(pos));
    } catch {}
  }, [pos]);

  useEffect(() => {
    const onResize = () =>
      setPos((p) => ({
        x: Math.min(
          Math.max(OFFSET, p.x),
          window.innerWidth - current.w - OFFSET
        ),
        y: Math.min(
          Math.max(OFFSET, p.y),
          window.innerHeight - current.h - OFFSET
        ),
      }));

    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, [current]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging) return;
      e.preventDefault();

      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;

      const nextX = Math.min(
        window.innerWidth - current.w - OFFSET,
        Math.max(OFFSET, dragStart.current.left + dx)
      );

      const nextY = Math.min(
        window.innerHeight - current.h - OFFSET,
        Math.max(OFFSET, dragStart.current.top + dy)
      );

      setPos({ x: nextX, y: nextY });
    };

    const onUp = (e) => {
      if (dragging && handleRef.current) {
        try {
          handleRef.current.releasePointerCapture(e.pointerId);
        } catch {}
      }
      setDragging(false);
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp, { passive: true });
    window.addEventListener("pointercancel", onUp, { passive: true });

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragging, current]);

  const startDrag = (e) => {
    if (!wrapperRef.current || !e.isPrimary) return;
    e.preventDefault();

    try {
      handleRef.current?.setPointerCapture(e.pointerId);
    } catch {}

    const rect = wrapperRef.current.getBoundingClientRect();
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      left: rect.left,
      top: rect.top,
    };

    setDragging(true);
  };

  const opts = useMemo(
    () => ({
      height: "100%",
      width: "100%",
      host: "https://www.youtube-nocookie.com",
      playerVars: {
        autoplay: 0,
        playsinline: 1,
        modestbranding: 1,
        rel: 0,
        controls: 1,
        iv_load_policy: 3,
        cc_load_policy: 0,
        origin: window.location.origin,
      },
    }),
    []
  );

  useEffect(() => {
    const onFsChange = () => {
      const exited = !document.fullscreenElement;

      if (exited && fsWasPlayingRef.current) {
        try {
          window.__kk_yt_player?.playVideo?.();
        } catch {}
      }

      fsWasPlayingRef.current = false;
    };

    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  if (!isVisible || !videoId) return null;

  const btnStyle = (right) => ({
    position: "absolute",
    top: 6,
    right,
    zIndex: 5,
    width: 28,
    height: 28,
    borderRadius: 9999,
    display: "grid",
    placeItems: "center",
    WebkitTapHighlightColor: "transparent",
    background: "rgba(0,0,0,0.4)",
    color: "white",
    fontSize: 11,
    lineHeight: 1,
    pointerEvents: "auto",
    border: "none",
    cursor: "pointer",
    backdropFilter: "blur(4px)",
  });

  return (
    <div
      ref={wrapperRef}
      className="fixed z-[9998] rounded-2xl overflow-hidden shadow-2xl"
      style={{
        left: pos.x,
        top: pos.y,
        width: current.w,
        height: current.h,
        pointerEvents: "auto",
        WebkitUserSelect: "none",
        userSelect: "none",
        background: "#000",
        transition: dragging ? "none" : "width 0.25s ease, height 0.25s ease",
      }}
      role="dialog"
      aria-label="Mini reproductor de video"
    >
      {/* Drag handle */}
      <div
        ref={handleRef}
        onPointerDown={startDrag}
        className="absolute left-0 top-0 w-full"
        style={{
          height: 36,
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "none",
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(0,0,0,0))",
          zIndex: 4,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          paddingTop: 5,
        }}
      >
        <span
          aria-hidden
          style={{
            width: 28,
            height: 3,
            borderRadius: 9999,
            background: "rgba(255,255,255,0.45)",
            display: "block",
          }}
        />
      </div>

      <button onClick={stop} aria-label="Cerrar" style={btnStyle(6)}>
        ✕
      </button>

      <button
        onClick={isExpanded ? minimize : expand}
        aria-label={isExpanded ? "Minimizar" : "Expandir"}
        style={btnStyle(40)}
      >
        {isExpanded ? "⤡" : "⤢"}
      </button>

      <button
        onClick={() => {
          try {
            if (isPlaying) {
              pause();
            } else {
              resume();
              setHasStarted(true);
            }
          } catch {}
        }}
        aria-label="Reproducir/Pausar"
        style={btnStyle(74)}
      >
        {isPlaying ? "⏸" : "▶︎"}
      </button>

      {/* Player SIEMPRE montado mientras haya videoId */}
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          zIndex: 1,
        }}
      >
        <YouTube
          videoId={videoId}
          opts={opts}
          className="w-full h-full"
          iframeClassName="block w-full h-full"
          style={{ width: "100%", height: "100%", display: "block" }}
          onReady={(e) => {
            const yt = e.target;
            setPlayerObject(yt);
            window.__kk_yt_player = yt;

            try {
              const iframe = yt.getIframe();
              iframe.setAttribute(
                "allow",
                "autoplay; encrypted-media; picture-in-picture; clipboard-write"
              );
            } catch {}
          }}
          onStateChange={(e) => {
            if (e?.data === 1) {
              setHasStarted(true);
              setIsPlaying(true);
              fsWasPlayingRef.current = !!document.fullscreenElement;
            } else if (e?.data === 2 || e?.data === 0) {
              setIsPlaying(false);
              fsWasPlayingRef.current = false;
            }
          }}
        />
      </div>

      {/* Overlay custom */}
      {!hasStarted && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 3,
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(26,10,46,0.85))",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          <img
            src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
            alt="trailer"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: -1,
              opacity: 0.6,
            }}
          />

          <button
            onClick={() => {
              try {
                window.__kk_yt_player?.playVideo?.();
                setHasStarted(true);
                setIsPlaying(true);
              } catch (err) {
                console.log("Player todavía no está listo", err);
              }
            }}
            style={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "#D46BE0",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 0 24px rgba(212,107,224,0.6)",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <polygon points="6,3 20,12 6,21" />
            </svg>
          </button>

          <p
            style={{
              color: "white",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              opacity: 0.9,
            }}
          >
            PLAY
          </p>
        </div>
      )}
    </div>
  );
}
